import React, { useEffect, useState } from 'react';
import {
  Button, Table, Tab, Tabs, Badge,
} from 'react-bootstrap';
import _ from 'lodash';
import Error from '../../Components/Error';
import useModalState from '../../Hooks/useModalState';
import PricingService from '../../Services/PricingService';
import UserService from '../../Services/UserService';
import VehicleCategoryModal from './Components/VehicleCategoryModal';
import ServiceAreaModal from './Components/ServiceAreaModal';
import PredefinedRouteModal from './Components/PredefinedRouteModal';
import PriceCalculator from './Components/PriceCalculator';
import PricingReport from './Components/PricingReport';
import useAskAiSuggestion from '../../Components/ChatWidget/askAiSuggestions';

function Pricing() {
  const [vehicleCategories, setVehicleCategories] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [predefinedRoutes, setPredefinedRoutes] = useState([]);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('calculator');

  /**
   * WHAT TO ASK ON THE PRICING SCREEN. Not "what does BUD → town cost" — the
   * calculator on this tab answers that in two clicks, and that question is
   * already in the global list. The one thing the calculator makes you do
   * one category at a time is compare them, so that is the offer:
   * `get_vehicle_catalog` plus a single `pricing_calculate(mode='hourly')`
   * call, which takes a list of `vehicleCategoryIds`.
   *
   * 8 hours because that is the disposal product we actually sell (the F1
   * "driver waits" bookings), not a round number.
   */
  useAskAiSuggestion({
    label: 'Ask about pricing',
    question: 'Compare an 8-hour hire across every vehicle category',
    prompt: 'Price an 8-hour hourly hire in EUR for every vehicle category we sell and'
      + ' show them side by side, with the per-hour rate each works out to.',
  });

  // Modals
  const vehicleCategoryModal = useModalState();
  const serviceAreaModal = useModalState();
  const predefinedRouteModal = useModalState();

  const fetchVehicleCategories = async () => {
    try {
      const response = await PricingService.fetchVehicleCategories();
      setVehicleCategories(response?.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const fetchServiceAreas = async () => {
    try {
      const response = await PricingService.fetchServiceAreas();
      setServiceAreas(response?.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const fetchPredefinedRoutes = async () => {
    try {
      const response = await PricingService.fetchPredefinedRoutes();
      setPredefinedRoutes(response?.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const debouncedFetch = _.debounce(() => {
    fetchVehicleCategories();
    fetchServiceAreas();
    fetchPredefinedRoutes();
  }, 500);

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, []);

  const handleDeleteCategory = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this vehicle category?')) return;
    try {
      await PricingService.deleteVehicleCategory(id);
      fetchVehicleCategories();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleDeleteServiceArea = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this service area?')) return;
    try {
      await PricingService.deleteServiceArea(id);
      fetchServiceAreas();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleDeleteRoute = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this predefined route?')) return;
    try {
      await PricingService.deletePredefinedRoute(id);
      fetchPredefinedRoutes();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const getServiceAreaName = (id) => {
    if (!id) return null;
    const sa = serviceAreas.find((s) => s.Id === id);
    return sa ? sa.name : id;
  };

  const getRouteEndpointDisplay = (route, type) => {
    const serviceAreaId = type === 'origin' ? route.origin_service_area_id : route.destination_service_area_id;
    const endpoint = type === 'origin' ? route.origin : route.destination;

    if (serviceAreaId) {
      return getServiceAreaName(serviceAreaId);
    }
    // Fallback to endpoint address
    return endpoint?.address || 'Unknown';
  };

  return (
    <>
      {/* Hero Section */}
      <div className="hero-section mb-4">
        <div className="row align-items-center">
          <div className="col-lg-8 mb-3 mb-lg-0">
            <h1 className="display-5 fw-bold mb-3">
              Pricing
              {' '}
              <span className="hero-highlight">Engine</span>
            </h1>
            <p className="lead text-muted mb-4">
              Manage vehicle categories, service areas, and predefined routes
            </p>
          </div>
          <div className="col-lg-4 d-none d-lg-block text-end">
            <div className="hero-icon">
              <i className="fa fa-calculator fa-5x text-primary opacity-25" />
            </div>
          </div>
        </div>
      </div>

      <Error error={error} setError={setError} />

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        {/* Price Calculator Tab */}
        <Tab eventKey="calculator" title="Calculate Price">
          <PriceCalculator vehicleCategories={vehicleCategories} />
        </Tab>

        {/* Pricing Report Tab */}
        <Tab eventKey="report" title="Export Report">
          <PricingReport vehicleCategories={vehicleCategories} />
        </Tab>

        {/* Vehicle Categories Tab - Admin only */}
        {UserService.getInstance().canAccessModule('CanConfigurePricingEngine') && (
        <Tab eventKey="categories" title="Vehicle Categories">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Vehicle Categories</h4>
            <Button
              onClick={() => vehicleCategoryModal.show({})}
              variant="primary"
            >
              <i className="fa fa-plus me-2" />
              Add Category
            </Button>
          </div>

          <Table responsive striped bordered hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Capacity</th>
                <th>Luggage</th>
                <th>Pricing Bands</th>
                <th>Status</th>
                <th style={{ width: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vehicleCategories.map((category) => (
                <tr key={category.Id}>
                  <td>
                    <strong>{category.name}</strong>
                    {category.description && (
                      <small className="d-block text-muted">{category.description}</small>
                    )}
                  </td>
                  <td>{category.capacity}</td>
                  <td>{category.max_luggage}</td>
                  <td>
                    {category.pricing?.bands?.length > 0 ? (
                      <small>
                        {category.pricing.bands.length}
                        {' '}
                        bands configured
                      </small>
                    ) : (
                      <small className="text-muted">No bands</small>
                    )}
                  </td>
                  <td>
                    {category.active === 1 ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="secondary">Inactive</Badge>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => vehicleCategoryModal.show(category)}
                      >
                        <i className="fa fa-edit" />
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDeleteCategory(category.Id)}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {vehicleCategories.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    No vehicle categories found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Tab>
        )}

        {/* Service Areas Tab - Admin only */}
        {UserService.getInstance().canAccessModule('CanConfigurePricingEngine') && (
        <Tab eventKey="service-areas" title="Service Areas">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Service Areas</h4>
            <Button
              onClick={() => serviceAreaModal.show({})}
              variant="primary"
            >
              <i className="fa fa-plus me-2" />
              Add Service Area
            </Button>
          </div>

          <Table responsive striped bordered hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Is Base?</th>
                <th>Area</th>
                <th>Multiplier</th>
                <th>Flat Fee</th>
                <th>Status</th>
                <th style={{ width: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {serviceAreas.map((sa) => (
                <tr key={sa.Id}>
                  <td>
                    <strong>{sa.name}</strong>
                    {sa.description && (
                      <small className="d-block text-muted">{sa.description}</small>
                    )}
                  </td>
                  <td>
                    <Badge bg="info">
                      {sa.geographic_definition?.type || 'radius'}
                    </Badge>
                  </td>
                  <td>
                    {sa.is_base_location ? (
                      <Badge bg="primary">Base</Badge>
                    ) : (
                      <Badge bg="secondary">Area</Badge>
                    )}
                  </td>
                  <td>
                    {sa.geographic_definition?.type === 'radius' && (
                      <>
                        {sa.geographic_definition.radius_km}
                        {' '}
                        km radius
                      </>
                    )}
                  </td>
                  <td>
                    {sa.price_multiplier?.toFixed(2) || '1.00'}
                    x
                  </td>
                  <td>
                    {sa.flat_rate_eur ? (
                      <>
                        {sa.flat_rate_eur}
                        {' '}
                        EUR
                        <small className="text-muted d-block">
                          {sa.flat_rate_applies_to || 'origin'}
                        </small>
                      </>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {sa.active === 1 ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="secondary">Inactive</Badge>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => serviceAreaModal.show(sa)}
                      >
                        <i className="fa fa-edit" />
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDeleteServiceArea(sa.Id)}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {serviceAreas.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted">
                    No service areas found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Tab>
        )}

        {/* Predefined Routes Tab - Admin only */}
        {UserService.getInstance().canAccessModule('CanConfigurePricingEngine') && (
        <Tab eventKey="routes" title="Predefined Routes">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Predefined Routes (Fixed Prices)</h4>
            <Button
              onClick={() => predefinedRouteModal.show({})}
              variant="primary"
            >
              <i className="fa fa-plus me-2" />
              Add Route
            </Button>
          </div>

          <Table responsive striped bordered hover>
            <thead>
              <tr>
                <th>Route Name</th>
                <th>Origin</th>
                <th>Destination</th>
                <th>Bidirectional</th>
                <th>Status</th>
                <th style={{ width: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {predefinedRoutes.map((route) => (
                <tr key={route.Id}>
                  <td>
                    <strong>{route.name}</strong>
                    {route.description && (
                      <small className="d-block text-muted">{route.description}</small>
                    )}
                  </td>
                  <td>{getRouteEndpointDisplay(route, 'origin')}</td>
                  <td>{getRouteEndpointDisplay(route, 'destination')}</td>
                  <td>
                    {(route.bidirectional || route.is_bidirectional) ? (
                      <Badge bg="info">Both Ways</Badge>
                    ) : (
                      <Badge bg="secondary">One Way</Badge>
                    )}
                  </td>
                  <td>
                    {route.active === 1 ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="secondary">Inactive</Badge>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => predefinedRouteModal.show(route)}
                      >
                        <i className="fa fa-edit" />
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDeleteRoute(route.Id)}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {predefinedRoutes.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No predefined routes found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Tab>
        )}
      </Tabs>

      {/* Modals */}
      {vehicleCategoryModal.isVisible && (
        <VehicleCategoryModal
          originalCategory={vehicleCategoryModal.selectedObject}
          show={vehicleCategoryModal.isVisible}
          fetchCategories={fetchVehicleCategories}
          handleClose={vehicleCategoryModal.hide}
          allCategories={vehicleCategories}
        />
      )}

      {serviceAreaModal.isVisible && (
        <ServiceAreaModal
          originalServiceArea={serviceAreaModal.selectedObject}
          show={serviceAreaModal.isVisible}
          fetchServiceAreas={fetchServiceAreas}
          handleClose={serviceAreaModal.hide}
        />
      )}

      {predefinedRouteModal.isVisible && (
        <PredefinedRouteModal
          originalRoute={predefinedRouteModal.selectedObject}
          show={predefinedRouteModal.isVisible}
          fetchRoutes={fetchPredefinedRoutes}
          handleClose={predefinedRouteModal.hide}
          vehicleCategories={vehicleCategories}
          serviceAreas={serviceAreas}
        />
      )}
    </>
  );
}

export default Pricing;
